<template>
  <li v-if="!isHidden" class="row" @click="navigateToDetailsPage(purchaseOrder.PurchaseOrderId)">
    <div class="title">
      <strong>{{ purchaseOrder.MetaData.Name }}</strong
      ><br />
      <span class="metadata">{{ formattedDate }}</span
      ><br />
      <small class="metadata">{{ purchaseOrder.Details.Car.CatalogCarName }}</small
      ><br />
      <small class="metadata">{{ carColor }}</small>
    </div>
    <div class="amount">
      {{ purchaseOrder.Details.Car.Amount }} <br />
      <span class="metadata">{{ komNumbersPreview }}</span>
    </div>

    <div class="customer">
      <div v-if="!!customerName" :title="customerName">
        {{ truncateName(customerName) }}
      </div>
      <div v-if="!!companyName" :title="companyName">
        {{ truncateName(companyName) }}
      </div>
      <div v-if="!!leaseCompanyName" :title="leaseCompanyName">
        {{ truncateName(leaseCompanyName) }}
      </div>
    </div>
    <div class="salesemployee">
      {{ purchaseOrder.Details.Employee.EmployeeName }}
    </div>
    <div class="status">
      <span>{{ purchaseOrder.Status }}</span> <br />
      <small class="metadata"> purchaseOrderId: {{ purchaseOrder.PurchaseOrderId }} </small>
    </div>
    <div class="actions">
      <app-button
        button-class="icon-stack"
        data-qa="purchase-order-remark-button"
        :disabled="!hasRemark"
        @click="toggleRemarkModal(true, $event)"
      >
        <span class="btn-icon">&#x2139;</span>
        <span class="btn-label">info</span>
      </app-button>
      <app-button
        button-class="icon-stack"
        data-qa="purchase-order-download-button"
        @click="downloadKumNumbers"
      >
        <span class="btn-icon">&#x1F4E5;</span>
        <span class="btn-label">download</span>
      </app-button>
      <app-button
        button-class="icon-stack"
        data-qa="purchase-order-delete-button"
        @click="toggleDeleteConfirmModal(true, $event)"
      >
        <span class="btn-icon">&#x1F5D1;</span>
        <span class="btn-label">archief</span>
      </app-button>
    </div>

    <modal
      v-if="showDeleteConfirmModal"
      :title="i18n.t('/Common/Confirmation')"
      @close="toggleDeleteConfirmModal(false, $event)"
    >
      <p>{{ i18n.t('/Dashboard/PurchaseOrders/ConfirmDeleteText') }}</p>
      <app-button
        data-qa="delete-purchase-order-cancel-button"
        @click="toggleDeleteConfirmModal(false, $event)"
        >{{ i18n.t('/Common/Cancel') }}</app-button
      >
      <app-button data-qa="delete-purchase-order-confirm-button" @click="confirmDelete">{{
        i18n.t('/Common/Delete')
      }}</app-button>
    </modal>

    <modal
      v-if="showRemarkModal"
      :title="i18n.t('/Dashboard/RemarkModalTitle')"
      @close="toggleRemarkModal(false, $event)"
    >
      <p>
        {{ purchaseOrder.Details.Remark }}
      </p>
    </modal>
  </li>
</template>

<script>
import Modal from '@/components/generic/presentational/molecules/modal/Modal.vue'
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import purchaseOrderService from '@/services/purchaseOrderService.js'
import DateHelper from '@/helpers/dateHelper'

export default {
  components: {
    Modal,
    AppButton,
  },
  props: {
    purchaseOrder: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      showDeleteConfirmModal: false,
      showRemarkModal: false,
      isHidden: false,
    }
  },
  computed: {
    customerName() {
      if (this.purchaseOrder.Details.Customer != null) {
        const initials = this.purchaseOrder.Details.Customer.CustomerInitials || ''
        const familyName = this.purchaseOrder.Details.Customer.CustomerFamilyName || ''
        return initials.concat(' ', familyName)
      } else return ''
    },
    companyName() {
      if (this.purchaseOrder.Details.CustomerCompany != null) {
        return this.purchaseOrder.Details.CustomerCompany.CustomerCompanyName
      } else return ''
    },
    leaseCompanyName() {
      return this.purchaseOrder.Details.LeaseCompany?.Name || ''
    },
    carColor() {
      if (this.purchaseOrder.Details.Car.Color != null)
        return this.purchaseOrder.Details.Car.Color.Name
      else return ''
    },
    komNumbersPreview() {
      const amount = Math.min(this.validCarStatuses.length, 2)
      let formattedKomnumbers = ''
      for (let i = 0; i < amount; i++) {
        formattedKomnumbers += this.validCarStatuses[i].KOMNumber.Number + '\n'
      }

      if (this.validCarStatuses.length > 3) {
        formattedKomnumbers += '...'
      } else if (this.validCarStatuses.length == 3) {
        formattedKomnumbers += this.validCarStatuses[2].KOMNumber.Number
      }

      return formattedKomnumbers
    },
    validCarStatuses() {
      return this.purchaseOrder.CarStatuses.filter((status) => status?.KOMNumber?.Number)
    },
    establishmentId() {
      return this.purchaseOrder.Details.Dealer?.EstablishmentID
    },
    formattedDate() {
      return DateHelper.formatDate(new Date(this.purchaseOrder.MetaData.LastUpdatedOn))
    },
    hasRemark() {
      return this.purchaseOrder.Details.Remark != null && this.purchaseOrder.Details.Remark != ''
    },
  },
  methods: {
    navigateToDetailsPage(purchaseOrderId) {
      this.$router.push({
        name: `purchase-order-page`,
        params: {
          purchaseOrderId: purchaseOrderId,
          establishmentId: this.establishmentId,
        },
      })
    },
    toggleRemarkModal(showModal, e) {
      e.stopPropagation()
      if (this.hasRemark) this.showRemarkModal = showModal
    },
    deletePurchaseOrder(e) {
      e.stopPropagation()
      this.toggleDeleteConfirmModal(true)
    },
    downloadKumNumbers(e) {
      e.stopPropagation()
      purchaseOrderService.downloadKomNumbers(this.purchaseOrder.PurchaseOrderId)
    },
    toggleDeleteConfirmModal(showModal, e) {
      e.stopPropagation()
      this.showDeleteConfirmModal = showModal
    },
    confirmDelete(e) {
      e.stopPropagation()
      const ponId = window.info.user.UserInfo.UserName
      purchaseOrderService.deletePurchaseOrder(this.purchaseOrder.PurchaseOrderId, ponId)
      this.isHidden = true
    },
    truncateName(name) {
      if (name.length > 20) {
        return name.substring(0, 20) + '...'
      } else {
        return name
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.metadata {
  color: #a3b8c0;
}

.actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.title,
.customer,
.salesemployee {
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}
</style>
